import autoTable from 'jspdf-autotable';
import { Surplus } from 'lib/api/reports/types';
import {
  formatDate,
  formatMoney,
  formatPercentage,
  getShortString,
} from 'lib/functions/utils';
import {
  BasePDFGenerator,
  PDFGeneratorConfig,
} from 'pages/Finance/Components/Financing/common/reportUtils/base';

interface SurplusReportConfig extends PDFGeneratorConfig {
  surplus: Surplus;
}

export class SurplusReportGenerator extends BasePDFGenerator {
  private surplus: Surplus;
  private summaryEndY: number = 35;

  constructor(config: SurplusReportConfig) {
    super(config);
    this.surplus = config.surplus;
  }

  private drawKeyValuePair(
    label: string,
    value: string | null | undefined,
    x: number,
    y: number,
    valueOffset: number = 35,
  ): number {
    if (value !== null && value !== undefined) {
      this.drawKeyValue(label, value, x, y, valueOffset);
      return y + 5;
    } else {
      this.drawKeyValue(label, '', x, y, valueOffset);
      return y + 5;
    }
  }

  protected addSummarySection(): void {
    const col1X = 10;
    const col2X = 110;
    let valueOffset = 35;
    let currentY = 35;

    // Sección: Documento
    this.drawText('Documento', col1X, currentY, { size: 12, bold: true });
    currentY += 10;

    // Primera columna - Información del documento
    let col1Y = currentY;
    col1Y = this.drawKeyValuePair(
      'Rut Cliente',
      this.surplus.businessIdentifier,
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'Nombre Cliente',
      getShortString(this.surplus.businessName, 25),
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'Folio',
      this.surplus.documentFolio,
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'ID Solicitud',
      String(this.surplus.factoringRequestId),
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'Estado Solicitud',
      this.surplus.factoringRequestStatus,
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'Fecha Operación',
      formatDate(new Date(this.surplus.factoringRequestOperationDate)),
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'Fecha Vencimiento',
      formatDate(new Date(this.surplus.documentPaymentDate)),
      col1X,
      col1Y,
      valueOffset,
    );
    col1Y = this.drawKeyValuePair(
      'Fecha Pago',
      this.surplus.documentPaidAt
        ? formatDate(new Date(this.surplus.documentPaidAt))
        : null,
      col1X,
      col1Y,
      valueOffset,
    );

    // Segunda columna - Montos del documento
    let col2Y = currentY;
    valueOffset = 55;
    col2Y = this.drawKeyValuePair(
      'Monto',
      formatMoney(this.surplus.documentAmount),
      col2X,
      col2Y,
      valueOffset,
    );
    col2Y = this.drawKeyValuePair(
      'Monto Anticipado',
      formatMoney(this.surplus.documentAdvanced),
      col2X,
      col2Y,
      valueOffset,
    );
    col2Y = this.drawKeyValuePair(
      'Monto Conciliado Documento',
      formatMoney(this.surplus.documentConciliationAmount),
      col2X,
      col2Y,
      valueOffset,
    );
    col2Y = this.drawKeyValuePair(
      'Monto Conciliado Mora',
      formatMoney(this.surplus.arrearConciliationAmount),
      col2X,
      col2Y,
      valueOffset,
    );
    col2Y = this.drawKeyValuePair(
      'Monto Conciliado Total',
      formatMoney(this.surplus.totalConciliationAmount),
      col2X,
      col2Y,
      valueOffset,
    );

    // Calcular la posición Y máxima para continuar
    const maxDocumentY = Math.max(col1Y, col2Y);
    this.summaryEndY = maxDocumentY + 10;
  }

  protected addContentSection(): void {
    let currentY = this.summaryEndY;
    const col1X = 10;
    const col2X = 110;
    const valueOffset = 60;

    // Sección: Excedente Retenido
    this.drawText('Excedente Retenido', col1X, currentY, {
      size: 12,
      bold: true,
    });
    currentY += 10;

    // Primera columna - Excedente Retenido
    let retainedCol1Y = currentY;
    retainedCol1Y = this.drawKeyValuePair(
      'Porcentaje Retenido',
      formatPercentage(this.surplus.retainedPercentage.toString()),
      col1X,
      retainedCol1Y,
      valueOffset,
    );
    retainedCol1Y = this.drawKeyValuePair(
      'Monto Retenido',
      formatMoney(this.surplus.retainedAmount),
      col1X,
      retainedCol1Y,
      valueOffset,
    );
    retainedCol1Y = this.drawKeyValuePair(
      'Monto Disponible A Liberar',
      formatMoney(this.surplus.availableSurplus),
      col1X,
      retainedCol1Y,
      valueOffset,
    );

    // Segunda columna - Reducciones
    let retainedCol2Y = currentY;
    retainedCol2Y = this.drawKeyValuePair(
      'Monto Reducido Por Mora',
      formatMoney(this.surplus.surplusReducedByArrear),
      col2X,
      retainedCol2Y,
      valueOffset,
    );
    retainedCol2Y = this.drawKeyValuePair(
      'Monto Reducido Por Documento',
      formatMoney(this.surplus.surplusReducedByDocument),
      col2X,
      retainedCol2Y,
      valueOffset,
    );
    retainedCol2Y = this.drawKeyValuePair(
      'Monto Reducido Total',
      formatMoney(
        this.surplus.surplusReducedByArrear +
          this.surplus.surplusReducedByDocument,
      ),
      col2X,
      retainedCol2Y,
      valueOffset,
    );

    const maxRetainedY = Math.max(retainedCol1Y, retainedCol2Y);
    currentY = maxRetainedY + 15;

    // Sección: Excedente Liberado
    this.drawText('Excedente Liberado', col1X, currentY, {
      size: 12,
      bold: true,
    });
    currentY += 10;

    let releasedY = currentY;
    releasedY = this.drawKeyValuePair(
      'Folio',
      this.surplus.surplusDocumentFolio || null,
      col1X,
      releasedY,
      valueOffset,
    );
    releasedY = this.drawKeyValuePair(
      'Monto Liberado',
      this.surplus.surplusDocumentAmount
        ? formatMoney(this.surplus.surplusDocumentAmount)
        : null,
      col1X,
      releasedY,
      valueOffset,
    );
    releasedY = this.drawKeyValuePair(
      'Fecha Liberación',
      this.surplus.surplusCreatedAt
        ? formatDate(new Date(this.surplus.surplusCreatedAt))
        : null,
      col1X,
      releasedY,
      valueOffset,
    );

    currentY = releasedY + 15;

    // Sección: Pago Excedente
    this.drawText('Pago Excedente', col1X, currentY, {
      size: 12,
      bold: true,
    });
    currentY += 10;

    let paymentY = currentY;
    paymentY = this.drawKeyValuePair(
      'Monto Pagado',
      this.surplus.surplusDocumentConciliationAmount
        ? formatMoney(this.surplus.surplusDocumentConciliationAmount)
        : null,
      col1X,
      paymentY,
      valueOffset,
    );
    paymentY = this.drawKeyValuePair(
      'Fecha Pago Total',
      this.surplus.surplusDocumentPaidAt
        ? formatDate(new Date(this.surplus.surplusDocumentPaidAt))
        : null,
      col1X,
      paymentY,
      valueOffset,
    );

    currentY = paymentY + 15;

    // Tablas de conciliaciones
    if (
      this.surplus.surplusDocumentConciliations &&
      this.surplus.surplusDocumentConciliations.length > 0
    ) {
      this.surplus.surplusDocumentConciliations.forEach((conciliation) => {
        // Verificar si necesitamos una nueva página
        if (currentY > 250) {
          this.doc.addPage();
          currentY = 20;
        }

        // Título de la conciliación
        this.drawText(
          `Devolución de pasivos ${conciliation.paymentRequest.id}`,
          col1X,
          currentY,
          { size: 11, bold: true },
        );
        currentY += 10;

        // Información de la conciliación
        let conciliationY = currentY;
        conciliationY = this.drawKeyValuePair(
          'Monto a girar devolución',
          formatMoney(conciliation.paymentRequest.amount),
          col1X,
          conciliationY,
          valueOffset,
        );
        conciliationY = this.drawKeyValuePair(
          'Fecha devolución',
          formatDate(new Date(conciliation.paymentRequest.executedAt)),
          col1X,
          conciliationY,
          valueOffset,
        );
        conciliationY = this.drawKeyValuePair(
          'Documentos a devolver',
          String(conciliation.paymentRequest.documentsToPay.length),
          col1X,
          conciliationY,
          valueOffset,
        );
        conciliationY = this.drawKeyValuePair(
          'Documentos aplicados',
          String(conciliation.paymentRequest.documentsApplied.length),
          col1X,
          conciliationY,
          valueOffset,
        );

        currentY = conciliationY + 10;

        // Tabla: Pasivos a devolver
        if (conciliation.paymentRequest.documentsToPay.length > 0) {
          this.drawText('Pasivos a devolver', col1X, currentY, {
            size: 10,
            bold: true,
          });
          currentY += 5;

          autoTable(this.doc, {
            startY: currentY,
            head: [
              [
                'Folio pasivo',
                'Rut acreedor',
                'Acreedor',
                'Rut deudor',
                'Deudor',
                'Monto documento',
                'Monto a pagar',
              ],
            ],
            body: conciliation.paymentRequest.documentsToPay.map((document) => [
              document.document.folio,
              document.document.businessIdentifier,
              getShortString(document.document.business.name, 20),
              document.document.stakeholderIdentifier,
              getShortString(document.document.stakeholder.name, 20),
              formatMoney(document.document.amount),
              formatMoney(document.amount),
            ]),
            theme: 'grid',
            headStyles: {
              fillColor: '#efefef',
              textColor: '#1b1b1b',
              fontStyle: 'bold',
              halign: 'left',
              valign: 'middle',
              lineColor: [44, 62, 80],
              fontSize: 8,
            },
            bodyStyles: {
              textColor: [50, 50, 50],
              fontSize: 7,
            },
            styles: {
              font: 'helvetica',
              lineColor: [44, 62, 80],
              lineWidth: 0.1,
            },
            columnStyles: {
              0: { cellWidth: 25 },
              1: { cellWidth: 25 },
              2: { cellWidth: 30 },
              3: { cellWidth: 25 },
              4: { cellWidth: 30 },
              5: { cellWidth: 30 },
              6: { cellWidth: 25 },
            },
          });

          // Obtener la posición Y después de la tabla
          const finalY =
            (this.doc as unknown as { lastAutoTable?: { finalY?: number } })
              .lastAutoTable?.finalY || currentY;
          currentY = finalY + 10;
        }

        // Tabla: Documentos aplicados
        if (conciliation.paymentRequest.documentsApplied.length > 0) {
          // Verificar si necesitamos una nueva página
          if (currentY > 250) {
            this.doc.addPage();
            currentY = 20;
          }

          this.drawText('Documentos aplicados', col1X, currentY, {
            size: 10,
            bold: true,
          });
          currentY += 5;

          autoTable(this.doc, {
            startY: currentY,
            head: [
              [
                'Folio documento',
                'Rut cliente',
                'Cliente',
                'Rut deudor',
                'Deudor',
                'Monto documento',
                'Monto a aplicar',
              ],
            ],
            body: conciliation.paymentRequest.documentsApplied.map(
              (document) => [
                document.document.folio,
                document.document.businessIdentifier,
                getShortString(document.document.business.name, 20),
                document.document.stakeholderIdentifier,
                getShortString(document.document.stakeholder.name, 20),
                formatMoney(document.document.amount),
                formatMoney(document.amount),
              ],
            ),
            theme: 'grid',
            headStyles: {
              fillColor: '#efefef',
              textColor: '#1b1b1b',
              fontStyle: 'bold',
              halign: 'left',
              valign: 'middle',
              lineColor: [44, 62, 80],
              fontSize: 8,
            },
            bodyStyles: {
              textColor: [50, 50, 50],
              fontSize: 7,
            },
            styles: {
              font: 'helvetica',
              lineColor: [44, 62, 80],
              lineWidth: 0.1,
            },
            columnStyles: {
              0: { cellWidth: 25 },
              1: { cellWidth: 25 },
              2: { cellWidth: 30 },
              3: { cellWidth: 25 },
              4: { cellWidth: 30 },
              5: { cellWidth: 30 },
              6: { cellWidth: 25 },
            },
          });

          // Obtener la posición Y después de la tabla
          const finalY =
            (this.doc as unknown as { lastAutoTable?: { finalY?: number } })
              .lastAutoTable?.finalY || currentY;
          currentY = finalY + 15;
        }
      });
    }
  }
}
